<template>
    <div
        v-auto-animate class="group"
        :class="isFullWidth ? 'w-full' : 'w-[48%]'"
        :id="`step-options-initials-button-${option?.label ?? mainPlacement.code}`"
        :style="{
            scrollMarginTop: `${scrollToOffset}px`,
        }"
    >
        <div v-if="hasImage"
            @click="selectStyle"
            :class="[
                'h-[128px] xs:h-36 bg-gray-F6F6F6 border border-b-0 cursor-pointer bg-no-repeat w-full',
                !hasErrorState && 'group-hover:border-black group-hover:border-b-0',
                hasErrorState ? 'border-red' : (option?.selected ? 'border-black' : 'border-gray-DFDFDF'),
                currentGroup === 'Character Stamping' && 'bg-top bg-cover'
            ]"
            :style="{
                backgroundImage: `url(&quot;${getImage(option)}&quot;)`,
                backgroundPosition: 'center',
                backgroundSize,
                backgroundRepeat: 'no-repeat',

            }"
        >
            <div v-if="hasZoomIn"
                @click.prevent.stop="handleOpenNestedPanel(option)"
                class="absolute top-[20px] left-[20px]"
            >
                <zoom-icon
                    class="size-[24px]"
                    :class="hasLockIcon ? 'text-gray-dark' : 'text-black'"
                />
            </div>
            <div v-if="hasLockIcon"
                class="absolute top-[17px] right-[20px]"
            >
                <lock-icon class="size-[24px] text-gray-dark" />
            </div>
            <div v-if="hasCheckmarkOnSelected && option?.selected" class="absolute top-[18px] right-[20px]">
                <img src="../../assets/images/icons/check-white.svg" alt="Checkmark" class="w-6 h-6 p-1 rounded-full bg-black">
            </div>
        </div>
        <button
            type="button"
            @click="selectStyle"
            :class="[
                hasErrorState ? 'border-red' : (option?.selected ? 'border-black' : 'border-gray-DFDFDF'),
                !hasImage && 'border-t',
                'border-b border-l border-r h-12 w-full flex items-center justify-between font-sans p-4 mb-[32px] text-[16px] tracking-[0.56px]',
                !hasErrorState && 'group-hover:border-black',
                'group-focus:border-l group-focus:border-r group-focus:border-t',
                'group-focus:outline-none group-focus:ring-1 group-focus:ring-black group-focus:border-black ring-offset-[-2px]'
            ]"
            :data-code="option?.raw?.code"
        >
            <div
                class="flex justify-between items-center w-full text-start leading-4 font-oceanside"
            >
                <div>
                    {{ getFormattedLabel(option?.label) }}
                    <span v-if="basePriceFormatted && !hasBoxStylePricing">
                        ({{ basePriceFormatted }})
                    </span>
                </div>
                <div
                    v-if="hasAddRemoveButton || shouldShowEditButton || hasBoxStylePricing"
                    class="flex items-center gap-4"
                >
                    <div v-if="basePriceFormatted && hasBoxStylePricing"
                        class="flex items-center justify-center px-[8px] whitespace-nowrap h-[24px] bg-gray-F0F0F0 text-[14px] rounded-[4px]"
                    >
                        {{ basePriceFormatted }}
                    </div>
                    <black-white-button
                        v-if="hasAddRemoveButton"
                        class="w-[90px] rounded-full font-semibold"
                        :is-selected="!option?.selected"
                        @click.stop.prevent="handleAddRemoveClick"
                    >
                        {{ option?.selected ? $t('REMOVE') : $t('ADD') }}
                    </black-white-button>
                    <button
                        v-if="shouldShowEditButton && option?.selected" @click="handleOpenInitialsOptionsPanel"
                        class="flex items-center gap-2"
                    >
                        <span class="underline">{{$t('EDIT')}}</span>
                        <edit-icon class="w-4 h-4" />
                    </button>
                </div>
            </div>
        </button>

        <slot></slot>
    </div>
</template>

<script>
    import BlackWhiteButton from './BlackWhiteButton.vue';
    import allStores from '../../stores/all';
    import getImage from '../../helpers/getimage';
    import ZoomIcon from '../ZoomIcon.vue';
    import LockIcon from '../LockIcon.vue';
    import EditIcon from '../EditIcon.vue';
    import { formatPrice } from '../../helpers/format';

    export default {
        mixins: [allStores],

        components: {
            BlackWhiteButton,
            ZoomIcon,
            LockIcon,
            EditIcon,
        },

        props: {
            hasLockIcon: {
                type: Boolean,
                default: false,
            },
            hasCheckmarkOnSelected: {
                type: Boolean,
                default: false,
            },
            mainPlacement: Object,
            allComponents: Array,
            option: Object,
            index: Number,
            scrollToTimeout: {
                type: Number,
                default: 400,
            },
            scrollToOffset: {
                type: Number,
                default: 0,
            },
            hasImage: {
                type: Boolean,
                default: true,
            },
            isDirty: {
                type: Boolean,
                default: false,
            },
            hasErrorState: {
                type: Boolean,
                default: false,
            },
            isFullWidth: {
                type: Boolean,
                default: true,
            },
            hasZoomIn: {
                type: Boolean,
                default: false,
            },
            zoomImageSizePercent: {
                type: Number,
                default: 100,
            },
            hasAddRemoveButton: {
                type: Boolean,
                default: false,
            },
            usePaletteColorSelection: {
                type: Boolean,
                default: false,
            },
            openWhenSelected: {
                type: Boolean,
                default: false,
            },
            shouldScrollToClickedButton: {
                type: Boolean,
                default: true,
            },
            removeSelectedOnAddRemoveClick: {
                type: Boolean,
                default: false,
            },
            shouldShowEditButton: {
                type: Boolean,
                default: false,
            },
            navigateOnClick: {
                type: Boolean,
                default: true,
            },
            hasBoxStylePricing: {
                type: Boolean,
                default: false,
            },
            showFreeUpchargePricing: {
                type: Boolean,
                default: false,
            },
            backgroundSize: {
                type: String,
                default: 'contain',
            },
        },

        data() {
            return {
            };
        },

        emits: ['select', 'unselect', 'open'],

        computed: {
            viewController() {
                return this.customizerStore.viewController;
            },

            isComplete() {
                return this.selectionsMade?.includes('Character Stamping');
            },

            panelsInErrorState() {
                return this.customizerStore.panelsInErrorState;
            },

            basePriceFormatted() {
                if (this.showFreeUpchargePricing) {
                    return `+${formatPrice(0, null, null, null, true)}`;
                }

                if (this.option == null) {
                    return null;
                }

                if (!this.option.raw) {
                    return null;
                }

                const price = this.customizerStore.priceFormatted(
                    this.customizerStore.getPrice(this.viewController, this.option.raw, 'basePrice'),
                    true,
                );

                if (this.customizerStore.leastRecentlyAddedPaintfillSection && ['Top', 'Back', 'Face/Sole'].includes(this.currentGroup)) {
                    // only show price if the paintfill section is the least recently used
                    if (this.customizerStore.leastRecentlyAddedPaintfillSection !== this.currentGroup) {
                        return null;
                    }
                }

                return price;
            },

            addonPriceFormatted() {
                if (!this.option.raw) {
                    return null;
                }

                return this.customizerStore.priceFormatted(
                    this.customizerStore.getPrice(this.viewController, this.option.raw, 'addonPrice'),
                    true,
                );
            },

            currentComponent() {
                return this.viewController.selectedAtPlacement(this.mainPlacement.code);
            },

            hasText1() {
                return this.currentComponent?.custom?.['v-stamping-has-text-1'] === 'yes';
            },

            hasText2() {
                return this.currentComponent?.custom?.['v-stamping-has-text-2'] === 'yes';
            },

            resolvedText1Caption() {
                if (this.hasText2) {
                    return 'FIRST_LINE_COLORS';
                }

                return 'COLORS';
            },

            hasSymbols() {
                return this.currentComponent?.custom?.['v-stamping-has-symbols'] === 'yes';
            },

            hasAddons() {
                return !!this.currentComponent?.custom?.['v-stamping-supports-snow-symbol'];
            },

            hasOptions() {
                return this.hasText1 || this.hasText2 || this.hasSymbols || this.hasAddons;
            },

            addonsEnabled() {
                return this.currentComponent?.custom?.['v-stamping-use-symbols-addon'] === 'yes';
            },

            textCharacters() {
                return this.currentComponent?.custom['v-stamping-characters'];
            },

            allowedCharacters() {
                const characters = this.textCharacters;

                if (characters) {
                    const charsWithoutSpecialCharacters = characters.replace(/[^a-zA-Z0-9]/g, '');
                    return charsWithoutSpecialCharacters.split('|||').map((line) => line.split(''));
                }

                return null;
            },

            textLimit() {
                const agsCode = this.currentComponent.custom?.['v-ags-code'];

                if (agsCode) {
                    const limit = this.mainPlacement.custom?.[`v-${agsCode}-limit`];

                    if (limit != null) {
                        return +limit;
                    }
                }

                if (this.viewController.blueprint?.custom?.['v-stamping-limit'] != null) {
                    return this.viewController.blueprint?.custom?.['v-stamping-limit'];
                }

                const ownLimit = this.currentComponent?.custom?.['v-stamping-limit'];

                return +ownLimit;
            },

            selectionsMade() {
                return this.customizerStore.selectionsMade;
            },

            currentGroup() {
                return this.customizerStore.currentGroup;
            },

            groupPreviewImage() {
                return this.mainPlacement?.custom['v-group-preview'];
            },
        },

        mounted() {
        },

        unmounted() {
            this.customizerStore.removePanelFromErrorState('StampSymbols');
        },

        methods: {
            setSelectedGroup(group) {
                this.customizerStore.setCurrentGroup(group);
            },

            handleOpenNestedPanel(option) {
                this.appStore.openNestedPanel('zoomIn', {
                    option,
                    mainPlacement: this.mainPlacement,
                    imageSizePercent: this.zoomImageSizePercent,
                });
            },

            handleReset() {
                this.unselectStyle();
            },

            getFormattedLabel(label) {
                if (label === 'No Thanks') {
                    return 'Stock Paintfill';
                }
                return label;
            },

            scrollToClickedButton() {
                setTimeout(() => {
                    const button = document.getElementById(`step-options-initials-button-${this.option.label}`);
                    button.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                        alignToTop: true,
                        inline: 'end',
                    });
                }, this.scrollToTimeout);
            },

            async selectStyle() {
                if (!this.navigateOnClick) {
                    return;
                }

                // Add/remove panels (Front sections) should toggle using the explicit selected state.
                // Fallback to the existing active-component heuristic for other contexts.
                if ((this.hasAddRemoveButton && this.option?.selected)) {
                    if (this.openWhenSelected) {
                        this.$emit('open');
                        return;
                    }

                    this.$emit('unselect');
                    return;
                }

                this.$emit('select', {
                    placement: this.mainPlacement.code,
                    option: this.option.value,
                });

                if (this.shouldScrollToClickedButton) {
                    this.scrollToClickedButton();
                }
            },

            handleAddRemoveClick() {
                if (this.removeSelectedOnAddRemoveClick && this.option?.selected) {
                    this.$emit('unselect');
                    return;
                }

                this.selectStyle();
            },

            async unselectStyle() {
                this.$emit('unselect');
            },

            updateAddons({ target }) {
                const isOpen = target.checked;

                if (isOpen && this.isDirty) {
                    this.customizerStore.addPanelToErrorState('StampSymbols');
                }

                this.customizerStore.useStampingAddons(this.mainPlacement.code, isOpen);
            },

            getFlightLinesImage(option) {
                return getImage(this.viewController, option.raw);
            },

            getImage(option) {
                if (option == null) {
                    return '';
                }

                if (option?.code?.includes('FlightLine')) {
                    return this.getFlightLinesImage(option);
                }

                if (this.groupPreviewImage) {
                    return this.groupPreviewImage;
                }

                return getImage(this.viewController, option.raw);
            },
        },
    };
</script>

<style scoped>
#cz3 .flex-wrap {
    flex-wrap: wrap !important;
}
</style>
